import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Modal from '../components/shared/Modal';

const ConfirmDialogContext = createContext(null);

export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  return context;
};

export const ConfirmDialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const closeDialog = useCallback((confirmed) => {
    setDialog((current) => {
      if (current?.resolve) current.resolve(Boolean(confirmed));
      return null;
    });
  }, []);

  const confirmDialog = useCallback((options = {}) => new Promise((resolve) => {
    setDialog({
      title: options.title || 'Please confirm',
      message: options.message || 'Are you sure you want to continue?',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      tone: options.tone || 'primary',
      resolve,
    });
  }), []);

  useEffect(() => () => {
    setDialog((current) => {
      if (current?.resolve) current.resolve(false);
      return null;
    });
  }, []);

  const confirmButtonClass =
    dialog?.tone === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : dialog?.tone === 'warning'
        ? 'bg-amber-500 hover:bg-amber-600'
        : dialog?.tone === 'info'
          ? 'bg-blue-600 hover:bg-blue-700'
          : 'bg-emerald-600 hover:bg-emerald-700';

  const value = useMemo(() => ({ confirmDialog }), [confirmDialog]);

  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      <Modal
        open={Boolean(dialog)}
        onClose={() => closeDialog(false)}
        title={dialog?.title || 'Please confirm'}
        size="sm"
        footer={(
          <>
            <button
              type="button"
              onClick={() => closeDialog(false)}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {dialog?.cancelText || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => closeDialog(true)}
              className={`px-4 py-2 text-sm text-white rounded-lg transition ${confirmButtonClass}`}
            >
              {dialog?.confirmText || 'Confirm'}
            </button>
          </>
        )}
      >
        <p className="text-sm text-gray-700 leading-relaxed">{dialog?.message}</p>
      </Modal>
    </ConfirmDialogContext.Provider>
  );
};

export default ConfirmDialogContext;
